import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Calendar from "../Calendar/Calendar";
import apiClient from "@/api/client";
import imageOne from "../../resource/meditate/internalRetreats/1.png";
import imageFour from "../../resource/meditate/internalRetreats/7.png";
import imageSix from "../../resource/meditate/internalRetreats/3.png";
import imageSeven from "../../resource/meditate/internalRetreats/4.png";
import imageEight from "../../resource/meditate/internalRetreats/8.png";
import imageThree from "../../resource/meditate/internalRetreats/2.png";
import imageFive from "../../resource/meditate/internalRetreats/5.png";
import imageNine from "../../resource/meditate/internalRetreats/9.png";

const InternalRetreats = () => {
  const navigate = useNavigate();
  const [retreats, setRetreats] = useState([]);
  const [allRetreats, setAllRetreats] = useState([]); // Store all retreats data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retreatDates, setRetreatDates] = useState([]); // Store dates that have retreats

  const getRetreatMarkers = () => {
    const markers = {};
    retreatDates.forEach((date) => {
      const dateKey = date.toISOString().split("T")[0]; // YYYY-MM-DD
      markers[dateKey] = "gold"; // choose your highlight color
    });
    return markers;
  };

  // Fetch retreats data from API
  useEffect(() => {
    const fetchRetreats = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/get-internal-retreats');

        if (response.status !== 200) {
          throw new Error("Failed to fetch retreats data");
        }

        const result = response.data; 

        // Store all retreats data
        setAllRetreats(result.data);

        // Generate array of all dates that have retreats
        const datesWithRetreats = [];
        result.data.forEach((retreat) => {
          const startDate = new Date(retreat.start_date);
          const endDate = new Date(retreat.end_date);

          // Add all dates between start and end date (inclusive)
          const currentDate = new Date(startDate);
          while (currentDate <= endDate) {
            datesWithRetreats.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
          }
        });

        setRetreatDates(datesWithRetreats);

        // Filter and sort retreats to show only nearest 4 events from current day
        const currentDate = new Date();
        const upcomingRetreats = result.data
          .filter((retreat) => {
            const startDate = new Date(retreat.start_date);
            return startDate >= currentDate; // Only future or today's events
          })
          .sort((a, b) => {
            const dateA = new Date(a.start_date);
            const dateB = new Date(b.start_date);
            return dateA - dateB; // Sort by start date ascending
          })
          .slice(0, 4); // Take only first 4

        setRetreats(upcomingRetreats);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching retreats:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRetreats();
  }, []);

  // Handle date selection from calendar (for showing retreat details if needed)
  const handleDateSelect = (date) => {
    console.log("Date selected:", date); // Debug log

    // Filter retreats for the selected date to show in table
    const selectedDateObj = new Date(date);
    const filteredRetreats = allRetreats.filter((retreat) => {
      const startDate = new Date(retreat.start_date);
      const endDate = new Date(retreat.end_date);

      // Normalize dates to compare only date parts (not time)
      const selectedDateNormalized = new Date(
        selectedDateObj.getFullYear(),
        selectedDateObj.getMonth(),
        selectedDateObj.getDate()
      );
      const startDateNormalized = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      );
      const endDateNormalized = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate()
      );

      // Check if selected date falls within the retreat date range
      return (
        selectedDateNormalized >= startDateNormalized &&
        selectedDateNormalized <= endDateNormalized
      );
    });

    // Update table to show retreats for selected date or all upcoming if none found
    if (filteredRetreats.length > 0) {
      setRetreats(filteredRetreats);
    } else {
      // If no retreats on selected date, show all upcoming
      resetToUpcomingRetreats();
    }
  };

  // Reset to show upcoming retreats
  const resetToUpcomingRetreats = () => {
    const currentDate = new Date();
    const upcomingRetreats = allRetreats
      .filter((retreat) => {
        const startDate = new Date(retreat.start_date);
        return startDate >= currentDate;
      })
      .sort((a, b) => {
        const dateA = new Date(a.start_date);
        const dateB = new Date(b.start_date);
        return dateA - dateB;
      })
      .slice(0, 4);

    setRetreats(upcomingRetreats);
  };

  const handleClick = (event) => {
    const pathMap = {
      itemBring: "/itemBring",
      timeTabel: "/timeTabel",
      internalRules: "/internalRules",
      internalRetreatForm: "/internalRetreatForm",
      internalRetreatRequest: "/internalRetreatRequest",
      InternalRetreatOrganiser: "/InternalRetreatOrganiser",
    };

    const path = event.currentTarget.id;
    const targetPath = pathMap[path];

    if (targetPath) {
      navigate(targetPath);
    }
  };

  const handleRetreatRequest = (retreatId, retreatNo) => {
    // Navigate to retreat request form with retreat ID
    navigate(
      `/internalRetreatRequest?retreatId=${retreatId}&retreatNo=${retreatNo}`
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };
    return date.toLocaleDateString("en-GB", options);
  };

  // Get status color class
  const getStatusColorClass = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "completed-color";
      case "scheduled":
      case "open":
        return "open-men-color";
      case "wait list":
      case "waitlist":
        return "wait-list-color";
      default:
        return "open-men-color";
    }
  };

  return (
    <div className="internalRetreat-page inside-pages">
      <div className="right-box">
        <img
          src={imageOne}
          alt="Right Top Image"
          className="right-image"
          width={300}
        />
      </div>

      <h1 className="font-main dark-gold p-4 mt-4">Internal Retreats</h1>
      <h1 className="font-second dark-gold p-4 mt-4">
        Conducted by Monastery Teachers
      </h1>

      <p className="mt-6 h4 p-4 text-justify">
        Internal Retreats are led by the Sangha at the Bhaddeka Vihari Monastery
        and are held on the premises. These retreats are designed to reflect
        various aspects of the Buddha's teachings.
      </p>
      <p className="mt-5 h4 p-4 text-justify">
        The programs include guided and silent meditation, walking meditation,
        and Dhamma discussions. They focus on both Tranquility (Samatha) and
        Insight (Vipassana) meditation, with retreat lengths ranging from one to
        ten days. Participants also have the opportunity to discuss their
        questions either within the group or individually with the teacher.
      </p>
      <p className="mt-5 h4 p-4 text-justify">
        To participate in an internal retreat, prior registration is required to
        secure a place. Please use the following link to access the Retreat
        Calendar and complete your online registration.
      </p>

      <h3 className="font-second dark-gold p-4 text-center mt-6">
        Retreats Calendar
      </h3>

      <div className="p-4 calander-box text-black align-item-center mt-5">
        <div className="calnder text-center mt-5">
          <Calendar
            onDateSelect={handleDateSelect}
             eventMarkers={getRetreatMarkers()} 
          />
        </div>
      </div>

      <div className="internel-table mt-6 p-4 text-black">
        {loading && (
          <div className="text-center p-4">
            <p>Loading retreats...</p>
          </div>
        )}

        {error && (
          <div className="text-center p-4">
            <p className="text-red-500">Error: {error}</p>
          </div>
        )}

        {!loading && !error && (
          <table>
            <thead>
              <tr className="text-bold">
                <th>Request for retreat</th>
                <th>Retreat No</th>
                <th>Course Type</th>
                <th>Dates</th>
                <th>Status</th>
                <th>Teacher's Name</th>
                <th>Organiser Contact No</th>
              </tr>
            </thead>
            <tbody>
              {retreats.length > 0 ? (
                retreats.map((retreat) => (
                  <tr key={retreat.id}>
                    <td className="text-center">
                      <img
                        src={imageFive}
                        alt="Retreat Request"
                        width={160}
                        onClick={() =>
                          handleRetreatRequest(retreat.id, retreat.retreat_no)
                        }
                        className="curser-pointer"
                      />
                    </td>
                    <td>{retreat.retreat_no}</td>
                    <td>{retreat.course_type}</td>
                    <td>
                      <span>{formatDate(retreat.start_date)}</span>
                      <br />
                      <span>{formatDate(retreat.end_date)}</span>
                    </td>
                    <td>
                      <span className={getStatusColorClass(retreat.status)}>
                        {retreat.status}
                      </span>
                    </td>
                    <td>{retreat.teachers_name}</td>
                    <td>{retreat.organiser_contact_no}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center p-4">
                    No upcoming retreats found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="btn-group2 mt-6">
        <img
          src={imageSix}
          alt="Internal Rules"
          width={300}
          onClick={handleClick}
          className="curser-pointer"
          id="internalRules"
        />
        <img
          src={imageFour}
          alt="Retreat Form"
          width={300}
          onClick={handleClick}
          className="curser-pointer"
          id="internalRetreatForm"
        />
      </div>

      <div className="btn-group2 mt-6">
        <img
          src={imageSeven}
          alt="Items to Bring"
          width={300}
          id="itemBring"
          className="curser-pointer"
          onClick={handleClick}
        />
        <img
          src={imageEight}
          alt="Time Table"
          width={300}
          id="timeTabel"
          onClick={handleClick}
          className="curser-pointer"
        />
      </div>

      <div className="mt-6 calander-box align-item-center p-4 text-center">
        <div className="c-details text-left">
          <h2 className="intro-text ">Contact Details</h2>
          <p className="font-p mt-4">Monastery : 035608 0918 / 0459 729 738</p>
          <p className="font-p">Mr Gamini : 0422 069 359</p>
          <p className="font-p">WhatsApp : 0422 069 359</p>
          <p className="font-p">E-mail : info@bvp.org.au</p>
        </div>
        <div className="d-button">
          <img
            src={imageNine}
            alt="Retreat Organiser"
            width={300}
            id="InternalRetreatOrganiser"
            className="curser-pointer"
            onClick={handleClick}
          />
        </div>
      </div>

      <div className="bottom-box mt-6">
        <img src={imageThree} alt="bottom-image" width={600} />
      </div>
    </div>
  );
};

export default InternalRetreats;
